import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { AxiosError } from 'axios'
import { useState, type FormEvent } from 'react'

import { FIELD_LABELS } from '@brickup/shared/locales/de'
import { AUTH } from '@brickup/shared/locales/de/auth'
import { COMMON } from '@brickup/shared/locales/de/common'
import { AUTH_USER_QUERY_KEY, login } from '@/api/auth'
import { TextInput } from '@/components/base'
import {
  PublicPageActions,
  PublicPageCard,
} from '@/components/Public/PublicPageCard'
import { useMutationErrorSnackbar } from '@/hooks/use-mutation-error-snackbar'

export const Route = createFileRoute('/login')({
  // Optional rather than `string | undefined`: a required key would force
  // every `to: '/login'` in the app to pass an empty target.
  validateSearch: (search: Record<string, unknown>): { target?: string } =>
    typeof search.target === 'string' ? { target: search.target } : {},
  component: LoginScreen,
})

function LoginScreen() {
  const { target } = Route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const showError = useMutationErrorSnackbar()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const { mutate, isPending } = useMutation({
    mutationFn: login,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: AUTH_USER_QUERY_KEY })
      setEmail('')
      setPassword('')
      // `target` is whatever path the guard interrupted, so it is a built href
      // rather than one of the router's known route ids.
      if (target) navigate({ href: target })
      else navigate({ to: '/' })
    },
    onError: (error: AxiosError) => {
      setPassword('')
      // Wrong credentials are the common case here, so that is the fallback
      // when the server sends no code of its own.
      showError(error, AUTH.loginFailed)
    },
  })

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (email && password) mutate({ email, password })
  }

  return (
    <PublicPageCard>
      {/* No heading: the card asks for one thing, and the button already says
          which. Every other screen out here names what it wants first. */}
      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <TextInput
          type="email"
          autoComplete="username"
          placeholder={FIELD_LABELS.email.label}
          value={email}
          disabled={isPending}
          onValueChange={value => setEmail(value ?? '')}
        />

        <TextInput
          type="password"
          autoComplete="current-password"
          placeholder={COMMON.password}
          value={password}
          disabled={isPending}
          onValueChange={value => setPassword(value ?? '')}
        />

        <PublicPageActions
          label={AUTH.login}
          loading={isPending}
          secondary={{ to: '/forgot-password', label: AUTH.forgotPassword }}
        />
      </form>
    </PublicPageCard>
  )
}
